import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown } from 'lucide-react';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import useOrgCurrency from '../../Hooks/useOrgCurrency';

const DEFAULT_LIMIT = 5;

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const TopAssetsByNbvWidget = ({ onAssetClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);
  const currency = useOrgCurrency();

  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const rows = useMemo(() => {
    const all = (data?.topAssets || []).slice(0, DEFAULT_LIMIT);
    const sorted = [...all].sort((a, b) => {
      const av = Number(a?.nbv) || 0;
      const bv = Number(b?.nbv) || 0;
      return sortDirection === 'asc' ? av - bv : bv - av;
    });
    return sorted;
  }, [data, sortDirection]);

  const toggleSort = () => setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));

  const isEmpty = rows.length === 0;
  const maxNbv = rows.reduce((max, row) => Math.max(max, Number(row?.nbv) || 0), 0);

  return (
    <ReportWidget
      title={t('business.inventory.top_assets_by_nbv', 'Top Assets by Net Book Value')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.top_assets_by_nbv.empty', 'No asset book value to display.')}
      onRefresh={handleRefresh}
    >
      <div className="table-responsive">
        <table className="table table-hover mb-0" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr className="text-muted">
              <th style={{ width: '5%' }}>#</th>
              <th>{t('shared.asset_name', 'Asset Name')}</th>
              <th
                style={{ cursor: 'pointer', userSelect: 'none', textAlign: 'right' }}
                onClick={toggleSort}
              >
                {t('business.inventory.asset_net_book_value', 'Asset Net Book Value')}
                {sortDirection === 'asc'
                  ? <ArrowUp size={12} className="ms-1" />
                  : <ArrowDown size={12} className="ms-1" />}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const nbv = Number(row?.nbv) || 0;
              const widthPct = maxNbv > 0 ? Math.max(4, Math.round((nbv / maxNbv) * 100)) : 0;
              return (
                <tr
                  key={row?.assetId ?? index}
                  onClick={() => row?.assetId && onAssetClick && onAssetClick(row.assetId)}
                  style={{ cursor: row?.assetId ? 'pointer' : 'default' }}
                >
                  <td className="fw-semibold text-muted">{index + 1}</td>
                  <td>
                    <div className="d-flex flex-column">
                      <span
                        className="fw-semibold text-truncate"
                        style={{ maxWidth: 280 }}
                        title={row?.name}
                      >
                        {row?.name || '—'}
                      </span>
                      <div
                        className="mt-1"
                        style={{
                          height: 4,
                          width: '100%',
                          maxWidth: 280,
                          backgroundColor: '#f3f4f6',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${widthPct}%`,
                            height: '100%',
                            backgroundColor: '#3b82f6',
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="fw-semibold text-end">{formatMoney(nbv, currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ReportWidget>
  );
};

export default TopAssetsByNbvWidget;
